import { Grid, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { goToClaimsBtn } from '../../../Constants/Constants';
import { IconStateType, ProviderInfo, StageNames } from '../../../types/type';
import { getNextView, getStagesInOrder } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ConfirmationHeader from '../../shared/ConfirmationHeader/ConfirmationHeader';
import ContactCard from '../../shared/ContactCard/ContactCard';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

interface SelectedProvider {
  provider: ProviderInfo;
  isStandalone?: boolean;
}

export default function AutoServiceConfirmation() {
  const theme = useTheme();
  const { state } = useLocation();
  const { provider, isStandalone } = state as SelectedProvider;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const currentStage = availableProgressStages.filter((stage) => stage.viewActive === true);
  const serviceType = provider.serviceTypeName;
  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getStagesInOrder(
    availableProgressStages.filter(
      (stage) =>
        currentStage.length > 0 &&
        stage.id !== currentStage[0].id &&
        !stage.completed &&
        stage.isEligible &&
        !stage.skipped
    )
  )[0];

  async function handleNext() {
    //Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === currentStage[0].id
          ? {
              ...stage,
              completed: true,
              viewActive: false,
              showProgress: true,
              skipped: false,
            }
          : stage;
      stage =
        stage.id === nextStage.id
          ? {
              ...stage,
              completed: false,
              viewActive: true,
              showProgress: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
    navigate(getNextView(nextStage.id));
  }

  async function goToClaimsOverview() {
    navigate('/claims-overview', {
      state: {
        isClaimRefreshNeeded: true,
      },
    });
  }

  let headerContent = '';
  let bodyContent;

  let nextButtonContent = '';
  if (nextStage) {
    nextButtonContent = 'Continue to ' + nextStage.name.toLowerCase();
  }
  switch (serviceType) {
    case StageNames.REPAIR:
      headerContent = "You've selected a repair shop.";
      bodyContent =
        'Your repair shop will contact you to schedule an estimate. However, you can expedite the process by calling them now.';
      break;
    case StageNames.RENTAL:
      headerContent = "You've selected a rental reservation.";
      bodyContent =
        "A representative from Enterprise Rent-A-Car will contact you to finalize the details about the type of vehicle and when you'll pick it up.";
      break;
    case StageNames.PHOTOS: {
      headerContent = `You've uploaded ${provider.numberUploaded} photo(s) to your claim.`;
      const failedPhotoItems = provider.failedPhotoItems || [];
      const failedPhotosNote = failedPhotoItems.length > 0 && (
        <Typography sx={{ paddingTop: '1em' }} variant="body1">
          The following photo{failedPhotoItems.length > 1 ? 's' : ''} could not be uploaded:{' '}
          {failedPhotoItems.map((item) => item.label).join(', ')}. You can try adding{' '}
          {failedPhotoItems.length > 1 ? 'them' : 'it'} again later from the Claim Details page.
        </Typography>
      );
      if (isStandalone) {
        bodyContent = (
          <>
            <Typography sx={{ paddingTop: '1em' }} variant="body1">
              Your photos are being processed. You'll be able to view them in your claim details soon.
            </Typography>
            {failedPhotosNote}
          </>
        );
      } else {
        bodyContent = (
          <>
            <Typography sx={{ paddingTop: '1em' }} variant="body1">
              Your photos are being processed. You'll be able to view them in your claim details soon.
            </Typography>
            {failedPhotosNote}
            <Typography sx={{ paddingTop: '1em' }} variant="body1">
              Select <b>Continue to Rental</b> to reserve a rental car to use while your vehicle is undergoing repairs.
            </Typography>
            <Typography sx={{ paddingTop: '1em' }} variant="body1">
              Don't want to reserve a rental car now? You can do it later from the Claim Details page.
            </Typography>
          </>
        );
      }

      break;
    }
    default:
      break;
  }

  const servicesContent = (
    <>
      {!isStandalone && <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader headerContent={headerContent} state={IconStateType.SUCCESS_STATE}></ConfirmationHeader>
        </Grid>
        {serviceType !== StageNames.PHOTOS && (
          <>
            <Grid sx={{ paddingBottom: '1rem' }} item xs={12}>
              <Typography sx={theme.custom.styles.serviceConfirmationHeader}>Details</Typography>
            </Grid>
            <Grid item xs={12}>
              <ContactCard isAuto={true} providerInfo={provider}></ContactCard>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.serviceConfirmationHeader,
              paddingTop: '1rem',
              marginTop: '0rem',
            }}
          >
            Next Steps
          </Typography>
        </Grid>

        <Grid sx={{ paddingTop: '1em' }} item xs={12}>
          <Typography variant="body1">{bodyContent}</Typography>
        </Grid>

        <Grid sx={{ marginTop: '3em' }} item xs={12}>
          <CallToAction
            nextButton={!!nextStage}
            nextButtonContent={nextButtonContent}
            handleNext={handleNext}
            backButton={false}
            skipButton={false}
            cancelButton={true}
            cancelButtonContent={goToClaimsBtn}
            handleCancel={goToClaimsOverview}
          ></CallToAction>
        </Grid>
      </Grid>
    </>
  );

  return <Box sx={{ width: '100%' }}>{servicesContent}</Box>;
}
